import { apiFetch } from "api/auth";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
}

interface ApiErrorResponse {
  success: false;
  message?: string;
  error?: string;
}

async function parseResponse<T>(res: Response): Promise<T> {
  const json = (await res.json()) as
    | ApiSuccessResponse<T>
    | ApiErrorResponse;
  if (!res.ok) {
    const msg =
      (json as ApiErrorResponse).message ||
      (json as ApiErrorResponse).error ||
      `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return (json as ApiSuccessResponse<T>).data;
}

// ---------------------------------------------------------------------------
// List leads
// ---------------------------------------------------------------------------

export interface LegalCertificateListItem {
  id: string;
  leadNo: string;
  clientName: string;
  email: string | null;
  phone: string | null;
  status: string;
  stage: string | null;
  markName: string | null;
  createdAt: string;
}

export interface LegalCertificateListResult {
  items: LegalCertificateListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type LegalCertificateStageFilter = "DISCUSSION" | "FILING" | "STATUS";
export type LegalCertificateSort = "newest" | "oldest";

export interface ListLegalCertificateLeadsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  stage?: LegalCertificateStageFilter;
  sort?: LegalCertificateSort;
}

export async function listLegalCertificateLeads(
  params: ListLegalCertificateLeadsParams = {}
): Promise<LegalCertificateListResult> {
  const q = new URLSearchParams();
  if (params.page != null) q.set("page", String(params.page));
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.search) q.set("search", params.search);
  if (params.status) q.set("status", params.status);
  if (params.stage) q.set("stage", params.stage);
  if (params.sort) q.set("sort", params.sort);

  const res = await apiFetch(
    `${API_BASE}/legal-certificate/leads?${q.toString()}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateListResult>(res);
}

// ---------------------------------------------------------------------------
// Next lead number
// ---------------------------------------------------------------------------

export async function getLegalCertificateNextLeadNumber(): Promise<{
  leadNo: string;
}> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/discussion/next-lead-number`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ leadNo: string }>(res);
}

// ---------------------------------------------------------------------------
// Get discussion details (edit / prefill mode)
// ---------------------------------------------------------------------------

export interface LegalCertificateApplicationItemView {
  tmNo: string | null;
  dateOfFiling: string | null;
  mark: string | null;
  typeOfMark: string | null;
  classNumber: number | null;
  userDate: string | null;
  validityDate: string | null;
}

export interface LegalCertificateApplicationDetailsView {
  numberOfApplications: number | null;
  designation: string | null;
  firmName: string | null;
  place: string | null;
  formOfAddress: string | null;
  applicantName: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
  applications: LegalCertificateApplicationItemView[];
}

export interface LegalCertificateDocumentView {
  documentId: string;
  fileKey: string;
  filename: string;
  downloadUrl: string;
}

export interface LegalCertificateDiscussionDetails {
  leadId: string;
  leadNo: string;
  module: string;
  stage: string | null;
  initiationType: string | null;
  referenceLeadId: string | null;
  referenceLead: { id: string; leadNo: string; module: string } | null;
  preliminary: {
    mark: string | null;
    contactName: string | null;
    contactPhone: string | null;
    email: string | null;
    callTimeDate: string | null;
  };
  applicationDetails: LegalCertificateApplicationDetailsView | null;
  documents: LegalCertificateDocumentView[];
}

export async function getLegalCertificateDiscussionDetails(
  leadId: string
): Promise<LegalCertificateDiscussionDetails> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/discussion/${leadId}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateDiscussionDetails>(res);
}

// ---------------------------------------------------------------------------
// Create preliminary discussion
// ---------------------------------------------------------------------------

export type LegalCertificateInitiationType =
  | "CLIENT_INITIATED"
  | "EXISTING_LEAD";

export interface CreateLegalCertificateDiscussionPayload {
  mark: string;
  contactName: string;
  contactPhone: string;
  email?: string;
  callTimeDate: string;
  initiationType: LegalCertificateInitiationType;
  referenceLeadId?: string;
}

export interface CreateLegalCertificateDiscussionResponse {
  leadId: string;
  leadNo: string;
  initiationType: LegalCertificateInitiationType;
  referenceLeadId: string | null;
}

export async function createLegalCertificateDiscussion(
  payload: CreateLegalCertificateDiscussionPayload
): Promise<CreateLegalCertificateDiscussionResponse> {
  const body: Record<string, unknown> = { ...payload };
  if (payload.initiationType === "CLIENT_INITIATED") {
    delete body.referenceLeadId;
  }
  const res = await apiFetch(`${API_BASE}/legal-certificate/discussion`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseResponse<CreateLegalCertificateDiscussionResponse>(res);
}

// ---------------------------------------------------------------------------
// Update preliminary discussion
// ---------------------------------------------------------------------------

export interface UpdateLegalCertificatePreliminaryPayload {
  mark?: string;
  contactName?: string;
  contactPhone?: string;
  email?: string;
  callTimeDate?: string;
}

export async function updateLegalCertificatePreliminary(
  leadId: string,
  payload: UpdateLegalCertificatePreliminaryPayload
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/discussion/${leadId}/preliminary`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string }>(res);
}

// ---------------------------------------------------------------------------
// Step 2: Application Details from Client — form submission + documents
// ---------------------------------------------------------------------------

export interface LegalCertificateApplicationInput {
  tmNo: string;
  dateOfFiling?: string | null;
  mark?: string | null;
  typeOfMark?: string | null;
  classNumber?: number | null;
  userDate?: string | null;
  validityDate?: string | null;
}

export interface SubmitLegalCertificateApplicationDetailsPayload {
  numberOfApplications?: number;
  designation?: string;
  firmName?: string;
  place?: string;
  formOfAddress?: string;
  applicantName?: string;
  applicantAddress?: string;
  applicantType?: string;
  signatoryName?: string;
  signatoryDesignation?: string;
  applications?: LegalCertificateApplicationInput[];
}

export async function submitLegalCertificateApplicationDetails(
  leadId: string,
  payload: SubmitLegalCertificateApplicationDetailsPayload
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/discussion/${leadId}/application-details`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string }>(res);
}

export interface LegalCertificateUploadPresignedFile {
  key: string;
  bucket: string;
  url: string;
}

interface LegalCertificatePresignDocumentsResult {
  files: LegalCertificateUploadPresignedFile[];
}

export async function generateLegalCertificateUploadPresignedUrls(
  leadId: string,
  files: Array<{ filename: string; mimeType: string }>
): Promise<LegalCertificateUploadPresignedFile[]> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/discussion/${leadId}/documents/presign`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ files }),
    }
  );
  const data = await parseResponse<LegalCertificatePresignDocumentsResult>(res);
  return data.files;
}

export interface LegalCertificateDocumentRegistrationInput {
  fileKey: string;
  filename: string;
}

export async function registerLegalCertificateDocuments(
  leadId: string,
  documents: LegalCertificateDocumentRegistrationInput[]
): Promise<{ leadId: string; registeredCount: number }> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/discussion/${leadId}/documents`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ documents }),
    }
  );
  return parseResponse<{ leadId: string; registeredCount: number }>(res);
}

export async function deleteLegalCertificateDocument(
  leadId: string,
  documentId: string
): Promise<{ leadId: string; documentId: string }> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/discussion/${leadId}/documents/${documentId}`,
    { method: "DELETE", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ leadId: string; documentId: string }>(res);
}

// ---------------------------------------------------------------------------
// Static option lists — mirror Renewal/Assignment modules so the legal
// certificate discussion screens stay visually consistent.
// ---------------------------------------------------------------------------

export const LEGAL_CERTIFICATE_ADDRESSEE_DESIGNATIONS = [
  "The Proprietor",
  "The Managing Partner",
  "The Designated Partner",
  "The Managing Director",
  "The Managing Trustee",
  "The Chairman",
  "NAME",
] as const;

export const LEGAL_CERTIFICATE_APPLICANT_TYPES = [
  { value: "SOLE_PROPRIETORSHIP", label: "Sole Proprietorship" },
  { value: "TRUST", label: "Trust" },
  { value: "PARTNERSHIP_FIRM", label: "Partnership Firm" },
  { value: "SOCIETY", label: "Society" },
  { value: "PRIVATE_LIMITED_COMPANY", label: "Private Limited Company" },
  {
    value: "LIMITED_LIABILITY_PARTNERSHIP",
    label: "Limited Liability Partnership",
  },
  { value: "OTHERS", label: "Others" },
] as const;

export const LEGAL_CERTIFICATE_FORM_OF_ADDRESS_OPTIONS = [
  "Sir",
  "Madam",
] as const;

export const LEGAL_CERTIFICATE_TYPE_OF_MARK_OPTIONS = [
  "Word",
  "Logo",
  "Label",
  "Slogan",
] as const;

export const LEGAL_CERTIFICATE_CLASS_NUMBERS = Array.from(
  { length: 44 },
  (_, i) => i + 1
);

export const LEGAL_CERTIFICATE_PLACE_OPTIONS = [
  "Thiruvananthapuram",
  "Kollam",
  "Pathanamthitta",
  "Alappuzha",
  "Kottayam",
  "Idukki",
  "Ernakulam",
  "Thrissur",
  "Palakkad",
  "Malappuram",
  "Kozhikode",
  "Wayanad",
  "Kannur",
  "Kasaragod",
] as const;

// ---------------------------------------------------------------------------
// Workflow progress
// ---------------------------------------------------------------------------

export interface LegalCertificateProgressStep {
  slug: string;
  label: string;
  stage: string;
  stepType: string;
  parentStep?: string;
  isCompleted: boolean;
  isSkipped: boolean;
}

export interface LegalCertificateProgressResponse {
  currentStep: string | null;
  currentSection: string | null;
  maxStepReached: string | null;
  steps: LegalCertificateProgressStep[];
}

export async function getLegalCertificateProgress(
  leadId: string
): Promise<LegalCertificateProgressResponse> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/discussion/${leadId}/progress`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateProgressResponse>(res);
}

// ---------------------------------------------------------------------------
// Skip step (Progress button)
// ---------------------------------------------------------------------------

export interface SkipLegalCertificateStepResponse {
  step: string;
  status: string;
}

export async function skipLegalCertificateStep(
  leadId: string,
  step: string
): Promise<SkipLegalCertificateStepResponse> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/discussion/${leadId}/workflow/skip-step`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ step }),
    }
  );
  return parseResponse<SkipLegalCertificateStepResponse>(res);
}

// ---------------------------------------------------------------------------
// Step 3: Invoice to Client for Legal Certificate
// ---------------------------------------------------------------------------

export interface LegalCertificateInvoicePreview {
  /** Addressee designation (line 1). */
  designation: string | null;
  /** Firm Name (line 2). */
  firmName: string | null;
  /** Place (line 3). */
  place: string | null;
  /** Form of Address. */
  formOfAddress: string | null;
  /** Mark. */
  mark: string;
  /** Application Number (TM No.). */
  applicationNumber: string | null;
  /** Selected Type of Mark. */
  selectedTypeOfMark: string | null;
  /** Selected Class. */
  selectedClass: number | null;
  /** Date of Filing. */
  dateOfFiling: string | null;
  /** 5c) Validity Date of Registration (captured in Application Details). */
  validityDate: string | null;
  /** No of Applications. */
  noOfApplications: number;
  profFeePerApp: number;
  govtFeePerApp: number;
  totalProfFee: number;
  totalGovtFee: number;
  totalCharges: number;
  /** Discount per application. */
  discountProfFeeLegalCert: number;
  totalDiscount: number;
  /** Invoice Amount. */
  invoiceAmount: number;
  /** Invoice No. */
  invoiceNo: string | null;
  /** Date of Invoice. */
  invoiceDate: string | null;
  includeInvoiceNo: boolean;
  /** Collected Amount. */
  collectedAmount: number;
  /** Outstanding Amount. */
  outstandingAmount: number;
  invoiceDocumentUrl: string | null;
  invoiceDocumentFilename: string | null;
}

export async function getLegalCertificateInvoicePreview(
  leadId: string
): Promise<LegalCertificateInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/invoice/${leadId}/to-client/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateInvoicePreview>(res);
}

export interface SaveLegalCertificateInvoicePayload {
  invoiceDate?: string;
  discountProfFeeLegalCert?: number;
  includeInvoiceNo?: boolean;
  invoiceAmount?: number;
}

export async function saveLegalCertificateInvoice(
  leadId: string,
  payload: SaveLegalCertificateInvoicePayload
): Promise<LegalCertificateInvoicePreview & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/invoice/${leadId}/to-client/save`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<LegalCertificateInvoicePreview & { leadId: string }>(res);
}

export interface LegalCertificateInvoicePdfResponse {
  s3Key: string;
  downloadUrl: string;
  filename: string;
}

export async function generateLegalCertificateInvoicePdf(
  leadId: string
): Promise<LegalCertificateInvoicePdfResponse> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/invoice/${leadId}/to-client/generate`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateInvoicePdfResponse>(res);
}

export interface SendLegalCertificateInvoicePayload {
  sendWhatsApp?: boolean;
  sendEmail?: boolean;
}

export interface SendLegalCertificateInvoiceResponse {
  whatsAppLink?: string;
  emailSent?: boolean;
  leadId?: string;
}

export async function sendLegalCertificateInvoiceToClient(
  leadId: string,
  payload: SendLegalCertificateInvoicePayload = {}
): Promise<SendLegalCertificateInvoiceResponse> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/invoice/${leadId}/to-client/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<SendLegalCertificateInvoiceResponse>(res);
}

export interface LegalCertificateInvoiceEmailPreview {
  to: string | null;
  subject: string;
  body: string;
  filename: string | null;
  downloadUrl: string | null;
}

export interface LegalCertificateInvoicePaymentMessagePreview {
  message: string;
  waLink: string;
  emailPreview: LegalCertificateInvoiceEmailPreview;
}

export async function getLegalCertificateInvoicePaymentMessagePreview(
  leadId: string
): Promise<LegalCertificateInvoicePaymentMessagePreview> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/invoice/${leadId}/to-client/payment-message-preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateInvoicePaymentMessagePreview>(res);
}

export async function sendLegalCertificateInvoicePaymentMessage(
  leadId: string
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/invoice/${leadId}/to-client/send-payment-message`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ waLink: string }>(res);
}

// ---------------------------------------------------------------------------
// Step 4: Payment from Client for Legal Certificate
// ---------------------------------------------------------------------------

export interface RecordLegalCertificatePaymentPayload {
  /** Delta — amount received in this payment, added to any prior payments. */
  collectedAmount: number;
}

export interface LegalCertificatePaymentRecordResponse {
  invoiceAmount: number;
  collectedAmount: number;
  outstandingAmount: number;
}

export async function recordLegalCertificatePaymentFromClient(
  leadId: string,
  payload: RecordLegalCertificatePaymentPayload
): Promise<LegalCertificatePaymentRecordResponse> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/invoice/${leadId}/payment`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<LegalCertificatePaymentRecordResponse>(res);
}

// ---------------------------------------------------------------------------
// Stage 2, Step 1: Stamp Paper Details to Vendor for Legal Certificate
// ---------------------------------------------------------------------------

export interface LegalCertificateStampVendorOption {
  name: string;
  phone: string;
  isDefault?: boolean;
}

export interface LegalCertificateStampPaperPreview {
  applicantName: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  userDate: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
  applicationNumber: string | null;
  mark: string | null;
  dateOfFiling: string | null;
  poaStamps: number;
  stampVendorName: string | null;
  stampVendorPhone: string | null;
  message: string | null;
  waLink: string | null;
  stampVendors: LegalCertificateStampVendorOption[];
}

export interface SaveLegalCertificateStampPaperPayload {
  poaStamps?: number;
  stampVendorName?: string;
  stampVendorPhone?: string;
}

export async function getLegalCertificateStampPaperPreview(
  leadId: string
): Promise<LegalCertificateStampPaperPreview> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/filing/stamp-paper/${leadId}/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateStampPaperPreview>(res);
}

export async function saveLegalCertificateStampPaper(
  leadId: string,
  payload: SaveLegalCertificateStampPaperPayload
): Promise<LegalCertificateStampPaperPreview> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/filing/stamp-paper/${leadId}/save`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<LegalCertificateStampPaperPreview & { leadId: string }>(
    res
  );
}

export async function sendLegalCertificateStampPaperWhatsApp(
  leadId: string
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/filing/stamp-paper/${leadId}/send`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ waLink: string; leadId: string }>(res);
}

// ---------------------------------------------------------------------------
// Stage 2, Step 2: Stamp Paper from Vendor for Legal Certificate
// ---------------------------------------------------------------------------

export type LegalCertificateVendorPaymentStatus = "PAID" | "PENDING";

export interface LegalCertificateStampPaperFromVendorPreview {
  /** 51g) PoA stamp count — read-only, entered at Step 1. */
  poaStamps: number;
  /** Auto-calculated: poaStamps × Rs. 110/-. */
  calculatedVendorAmount: number;
  /** User override (51i). Null means use the calculated amount. */
  vendorAmountPayable: number | null;
  /** Effective amount payable — override if set, otherwise calculated. */
  effectiveVendorAmount: number;
  /** 51j) Paid to Vendor. */
  vendorPaid: boolean;
  vendorPaymentStatus: LegalCertificateVendorPaymentStatus;
}

export async function getLegalCertificateStampPaperFromVendorPreview(
  leadId: string
): Promise<LegalCertificateStampPaperFromVendorPreview> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/filing/stamp-paper-from-vendor/${leadId}/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateStampPaperFromVendorPreview>(res);
}

export interface SaveLegalCertificateStampPaperFromVendorPayload {
  /**
   * 51i) Amount Payable to Stamp Vendor. Send null to clear an override and
   * fall back to the auto-calculated amount.
   */
  vendorAmountPayable?: number | null;
  /** 51j) Paid to Vendor (true = paid, false = pending payment). */
  vendorPaid?: boolean;
}

export async function saveLegalCertificateStampPaperFromVendor(
  leadId: string,
  payload: SaveLegalCertificateStampPaperFromVendorPayload
): Promise<LegalCertificateStampPaperFromVendorPreview & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/filing/stamp-paper-from-vendor/${leadId}/save`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<
    LegalCertificateStampPaperFromVendorPreview & { leadId: string }
  >(res);
}

// ---------------------------------------------------------------------------
// Stage 2, Step 3: Preparation of Power of Attorney for Legal Certificate
// ---------------------------------------------------------------------------

export interface LegalCertificatePoaPdfMeta {
  key: string;
  url: string;
  filename: string;
}

export interface LegalCertificatePoaContext {
  applicantName: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
  mark: string | null;
  /** Editable — ISO date string (YYYY-MM-DD). */
  dateOfPowerOfAttorney: string | null;
  /** Read-only formatted date (e.g., "12th day of January 2025"). */
  dateOfPowerOfAttorneyFormatted: string | null;
  /** Editable. */
  descriptionOfApplicant: string | null;
  /** Derived from applicantType — used for backend PDF templating. */
  templateVariant: string | null;
  pdf: LegalCertificatePoaPdfMeta | null;
  pdfIsStale: boolean;
  pdfGeneratedAt: string | null;
  pdfGeneratedBy: string | null;
}

export interface SaveLegalCertificatePoaPayload {
  /** ISO date YYYY-MM-DD. Optional — only updates if provided. */
  dateOfPowerOfAttorney?: string;
  descriptionOfApplicant?: string;
}

export async function getLegalCertificatePoaContext(
  leadId: string
): Promise<LegalCertificatePoaContext> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/filing/poa/${leadId}/context`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificatePoaContext>(res);
}

export async function saveLegalCertificatePoa(
  leadId: string,
  payload: SaveLegalCertificatePoaPayload
): Promise<LegalCertificatePoaContext> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/filing/poa/${leadId}/save`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<LegalCertificatePoaContext>(res);
}

export async function generateLegalCertificatePoaPdf(
  leadId: string,
  force: boolean = false
): Promise<LegalCertificatePoaPdfMeta> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/filing/poa/${leadId}/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ force }),
    }
  );
  return parseResponse<LegalCertificatePoaPdfMeta>(res);
}

// ---------------------------------------------------------------------------
// Stage 2, Step 4: File forms for Legal Certificate
// ---------------------------------------------------------------------------

export interface LegalCertificateFileFormsApplicationItem {
  applicationId: string;
  tmNo: string | null;
  mark: string | null;
  /** 51k) No. of Registration Certificate. */
  noOfRegistrationCertificate: number | null;
  /** 51l) Date of Registration Certificate (YYYY-MM-DD). */
  dateOfRegistrationCertificate: string | null;
  /** 51m) Date of Filing of Legal Certificate (YYYY-MM-DD). */
  dateOfFilingLegalCertificate: string | null;
  tmMFormPdfFileKey: string | null;
  tmMFormPdfFilename: string | null;
  /** Presigned download URL — null when no PDF has been uploaded yet. */
  pdfDownloadUrl: string | null;
  isPdfUploaded: boolean;
}

export interface LegalCertificateFileFormsResponse {
  applications: LegalCertificateFileFormsApplicationItem[];
}

export interface SaveLegalCertificateFileFormsItem {
  applicationId: string;
  /** Pass null to clear; omit to leave unchanged. */
  noOfRegistrationCertificate?: number | null;
  /** YYYY-MM-DD; null clears; omit to leave unchanged. */
  dateOfRegistrationCertificate?: string | null;
  /** YYYY-MM-DD; null clears; omit to leave unchanged. */
  dateOfFilingLegalCertificate?: string | null;
  tmMFormPdfFileKey?: string | null;
  tmMFormPdfFilename?: string | null;
}

export interface SaveLegalCertificateFileFormsPayload {
  applications: SaveLegalCertificateFileFormsItem[];
}

export async function getLegalCertificateFileForms(
  leadId: string
): Promise<LegalCertificateFileFormsResponse> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/filing/file-forms/${leadId}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateFileFormsResponse>(res);
}

export async function saveLegalCertificateFileForms(
  leadId: string,
  payload: SaveLegalCertificateFileFormsPayload
): Promise<LegalCertificateFileFormsResponse & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/filing/file-forms/${leadId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<LegalCertificateFileFormsResponse & { leadId: string }>(
    res
  );
}

// ---------------------------------------------------------------------------
// Stage 2, Step 5: Inform Client about Filing of Legal Certificate
// ---------------------------------------------------------------------------

export interface LegalCertificateInformClientOfFilingPreview {
  message: string;
  recipientPhone: string;
  applicationNumbers: string;
}

export interface LegalCertificateInformClientOfFilingSendResult {
  leadId: string;
  waLink: string;
  message: string;
  sentViaApi: boolean;
}

export async function getLegalCertificateInformClientOfFilingPreview(
  leadId: string
): Promise<LegalCertificateInformClientOfFilingPreview> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/filing/${leadId}/inform-client/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateInformClientOfFilingPreview>(res);
}

export async function sendLegalCertificateInformClientOfFilingWhatsApp(
  leadId: string
): Promise<LegalCertificateInformClientOfFilingSendResult> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/filing/${leadId}/inform-client/whatsapp`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateInformClientOfFilingSendResult>(res);
}

// ---------------------------------------------------------------------------
// Stage 3, Step 1: Legal Certificate Received (Status of Legal Certificate)
// ---------------------------------------------------------------------------

export interface LegalCertificateReceivedApplicationItem {
  /** legal_certificate_applications.id — used as applicationId in the payload. */
  applicationId: string;
  /** 3c) Application Number (TM No.). */
  tmNo: string | null;
  /** 1b) Mark. */
  mark: string | null;
  legalCertificatePdfFileKey: string | null;
  legalCertificatePdfFilename: string | null;
  legalCertificateDownloadUrl: string | null;
  isLegalCertificateUploaded: boolean;
  registrationCertificatePdfFileKey: string | null;
  registrationCertificatePdfFilename: string | null;
  registrationCertificateDownloadUrl: string | null;
  isRegistrationCertificateUploaded: boolean;
}

export interface LegalCertificateReceivedPreview {
  applicationDetailsId: string;
  /** One entry per TM application sourced from application details. */
  applications: LegalCertificateReceivedApplicationItem[];
  /** True when every application has a Legal Certificate PDF uploaded. */
  allLegalCertificatesUploaded: boolean;
  /** True when every application has a Registration Certificate PDF uploaded. */
  allRegistrationCertificatesUploaded: boolean;
  /** True when at least one Legal Certificate PDF is uploaded. */
  anyLegalCertificateUploaded: boolean;
}

export interface SaveLegalCertificateReceivedItem {
  applicationId: string;
  /** Pass null to clear; omit to leave unchanged. */
  legalCertificatePdfFileKey?: string | null;
  legalCertificatePdfFilename?: string | null;
  registrationCertificatePdfFileKey?: string | null;
  registrationCertificatePdfFilename?: string | null;
}

export interface SaveLegalCertificateReceivedPayload {
  applications: SaveLegalCertificateReceivedItem[];
}

export async function getLegalCertificateReceived(
  leadId: string
): Promise<LegalCertificateReceivedPreview> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/status/${leadId}/received`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateReceivedPreview>(res);
}

export async function saveLegalCertificateReceived(
  leadId: string,
  payload: SaveLegalCertificateReceivedPayload
): Promise<LegalCertificateReceivedPreview & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/status/${leadId}/received`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<LegalCertificateReceivedPreview & { leadId: string }>(
    res
  );
}

// ---------------------------------------------------------------------------
// Stage 3, Step 2: Prepare Kit – Covering Note for Legal Certificate
// ---------------------------------------------------------------------------

export interface LegalCertificateCoveringNotePdfMeta {
  key: string;
  url: string;
  filename: string;
}

export interface LegalCertificateCoveringNoteApplicationItem {
  applicationId: string;
  /** 1-based row index for the table (matches the row number in the PDF). */
  serial: number;
  /** 3c) Application Number (TM No.). */
  tmNo: string | null;
  /** 1b) Mark — falls back to application details mark when blank. */
  mark: string | null;
  /** 1s) Type of Mark — falls back to application details type when blank. */
  typeOfMark: string | null;
  /** 1r) Class number — falls back to application details class when blank. */
  classNumber: number | null;
  /** 3b) Date of Filing (YYYY-MM-DD) — falls back to application details date when blank. */
  dateOfFiling: string | null;
}

export interface LegalCertificateCoveringNoteContext {
  applicationDetailsId: string;
  /** 3d) Addressee fetched from application details (multi-line block). */
  addressee: string | null;
  /** 3f) Form of Address fetched from application details. */
  formOfAddress: string | null;
  /** 3c) Application Number (TM No.) — comma-separated list of all TM nos. */
  applicationNumber: string | null;
  /** 3b) Date of Filing fetched from application details. */
  dateOfFiling: string | null;
  /** 1b) Mark fetched from application details. */
  mark: string | null;
  /** 1s) Selected Type of Mark fetched from application details. */
  selectedTypeOfMark: string | null;
  /** 1r) Selected Class fetched from application details. */
  selectedClass: number | null;
  /** 1t) No of Applications fetched from application details. */
  numberOfApplications: number | null;
  /** 51n) Date of Note with Legal Certificate — entered by staff. */
  dateOfNoteWithLegalCertificate: string | null;
  /** 5c) Validity Date of Registration — entered/confirmed by staff. */
  validityDateOfRegistration: string | null;
  /** Per-application rows powering the read-only table. */
  applications: LegalCertificateCoveringNoteApplicationItem[];
  pdf: LegalCertificateCoveringNotePdfMeta | null;
  pdfIsStale: boolean;
  pdfGeneratedAt: string | null;
  pdfGeneratedBy: string | null;
}

export interface SaveLegalCertificateCoveringNotePayload {
  /** YYYY-MM-DD; null clears; omit to leave unchanged. */
  dateOfNoteWithLegalCertificate?: string | null;
  /** YYYY-MM-DD; null clears; omit to leave unchanged. */
  validityDateOfRegistration?: string | null;
}

export async function getLegalCertificateCoveringNote(
  leadId: string
): Promise<LegalCertificateCoveringNoteContext> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/status/${leadId}/covering-note`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateCoveringNoteContext>(res);
}

export async function saveLegalCertificateCoveringNote(
  leadId: string,
  payload: SaveLegalCertificateCoveringNotePayload
): Promise<LegalCertificateCoveringNoteContext & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/status/${leadId}/covering-note`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<
    LegalCertificateCoveringNoteContext & { leadId: string }
  >(res);
}

export async function generateLegalCertificateCoveringNotePdf(
  leadId: string,
  force: boolean = false
): Promise<LegalCertificateCoveringNotePdfMeta & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/status/${leadId}/covering-note/generate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ force }),
    }
  );
  return parseResponse<
    LegalCertificateCoveringNotePdfMeta & { leadId: string }
  >(res);
}

// ---------------------------------------------------------------------------
// Stage 3, Step 3: Hand Over Kit to Client
// ---------------------------------------------------------------------------

export interface LegalCertificateHandoverPreview {
  applicationDetailsId: string;
  applicationNumber: string | null;
  mark: string | null;
  /** True when the scanned Legal Certificate PDF has been uploaded (Step 1 prerequisite). */
  isLegalCertificateUploaded: boolean;
  /** True when the Covering Note PDF has been generated (Step 2 prerequisite). */
  isCoveringNoteGenerated: boolean;
  /** ISO timestamp when the kit was handed over to the client; null if not yet handed over. */
  handedOverToClientAt: string | null;
  isHandedOver: boolean;
}

export async function getLegalCertificateHandover(
  leadId: string
): Promise<LegalCertificateHandoverPreview> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/status/${leadId}/handover`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateHandoverPreview>(res);
}

export async function recordLegalCertificateHandover(
  leadId: string
): Promise<LegalCertificateHandoverPreview & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/status/${leadId}/handover`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ confirmed: true }),
    }
  );
  return parseResponse<LegalCertificateHandoverPreview & { leadId: string }>(
    res
  );
}

// ---------------------------------------------------------------------------
// Stage 3, Step 4: Notice to Client of Legal Certificate
// ---------------------------------------------------------------------------

export interface LegalCertificateNoticeToClientDocument {
  label: string;
  fileKey: string;
  filename: string;
}

export interface LegalCertificateNoticeToClientApplicationGroup {
  applicationId: string;
  tmNo: string | null;
  mark: string | null;
  legalCertificate: LegalCertificateNoticeToClientDocument | null;
  registrationCertificate: LegalCertificateNoticeToClientDocument | null;
}

export interface LegalCertificateNoticeToClientPreview {
  recipientPhone: string | null;
  recipientEmail: string | null;
  applicationNumbers: string;
  documents: LegalCertificateNoticeToClientDocument[];
  coveringNote: LegalCertificateNoticeToClientDocument | null;
  applications: LegalCertificateNoticeToClientApplicationGroup[];
}

export interface LegalCertificateNoticeToClientSendPayload {
  sendWhatsApp?: boolean;
  sendEmail?: boolean;
}

export interface LegalCertificateNoticeToClientSendResult {
  leadId: string;
  waLinks: string[];
  sentViaApi: boolean;
  emailsSent: number;
}

export async function getLegalCertificateNoticeToClientPreview(
  leadId: string
): Promise<LegalCertificateNoticeToClientPreview> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/status/${leadId}/notice-to-client/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateNoticeToClientPreview>(res);
}

export async function sendLegalCertificateNoticeToClient(
  leadId: string,
  payload: LegalCertificateNoticeToClientSendPayload = {}
): Promise<LegalCertificateNoticeToClientSendResult> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/status/${leadId}/notice-to-client/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<LegalCertificateNoticeToClientSendResult>(res);
}

// ---------------------------------------------------------------------------
// Stage 3, Step 5: Inform Client about Legal Certificate
// ---------------------------------------------------------------------------

export interface LegalCertificateInformClientApplicationStatus {
  applicationId: string;
  tmNo: string | null;
  mark: string | null;
  isLegalCertificateUploaded: boolean;
}

export interface LegalCertificateInformClientStatusPreview {
  message: string;
  recipientPhone: string;
  applicationNumbers: string;
  applications: LegalCertificateInformClientApplicationStatus[];
  allLegalCertificatesUploaded: boolean;
}

export interface LegalCertificateInformClientStatusSendResult {
  leadId: string;
  waLink: string;
  message: string;
  sentViaApi: boolean;
}

export async function getLegalCertificateInformClientStatusPreview(
  leadId: string
): Promise<LegalCertificateInformClientStatusPreview> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/status/${leadId}/inform-client/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateInformClientStatusPreview>(res);
}

export async function sendLegalCertificateInformClientStatus(
  leadId: string
): Promise<LegalCertificateInformClientStatusSendResult> {
  const res = await apiFetch(
    `${API_BASE}/legal-certificate/status/${leadId}/inform-client/whatsapp`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<LegalCertificateInformClientStatusSendResult>(res);
}

/** Backend LegalCertificate*Step enum slugs used for workflow progress lookup. */
export const LegalCertificateWorkflowStep = {
  LC_S1_01_PreliminaryDiscussion: "LC_S1_01_PRELIMINARY_DISCUSSION",
  LC_S1_02_ApplicationDetailsFromClient: "LC_S1_02_APPLICATION_DETAILS_FROM_CLIENT",
  LC_S1_03_InvoiceToClient: "LC_S1_03_INVOICE_TO_CLIENT",
  LC_S1_04_PaymentFromClient: "LC_S1_04_PAYMENT_FROM_CLIENT",
  LC_S2_01_StampPaperDetailsToVendor: "LC_S2_01_STAMP_PAPER_DETAILS_TO_VENDOR",
  LC_S2_02_StampPaperFromVendor: "LC_S2_02_STAMP_PAPER_FROM_VENDOR",
  LC_S2_03a_PoAFieldsSaved: "LC_S2_03A_POA_FIELDS_SAVED",
  LC_S2_03b_PoAPdfGenerated: "LC_S2_03B_POA_PDF_GENERATED",
  LC_S2_04_FileFormsForLegalCertificate: "LC_S2_04_FILE_FORMS_FOR_LEGAL_CERTIFICATE",
  LC_S2_05_InformClientOfFiling: "LC_S2_05_INFORM_CLIENT_OF_FILING",
  LC_S3_01_LegalCertificateReceived: "LC_S3_01_LEGAL_CERTIFICATE_RECEIVED",
  LC_S3_02a_CoveringNoteFieldsSaved: "LC_S3_02A_COVERING_NOTE_FIELDS_SAVED",
  LC_S3_02b_CoveringNotePdfGenerated: "LC_S3_02B_COVERING_NOTE_PDF_GENERATED",
  LC_S3_03_HandOverKitToClient: "LC_S3_03_HAND_OVER_KIT_TO_CLIENT",
  LC_S3_04_NoticeToClient: "LC_S3_04_NOTICE_TO_CLIENT",
  LC_S3_05_InformClientOfLegalCertificate: "LC_S3_05_INFORM_CLIENT_OF_LEGAL_CERTIFICATE",
} as const;
